import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation, HostListener, ChangeDetectorRef } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { environment } from "../../../environments/environment";
import { Service } from "../../models/service";
import { RoleControllerService } from "../../services/role-controller/role-controller.service";
import { Store } from "../../services/store.service";
import { ServicePropertiesControllerService } from "../../services/service-controller/service-properties-controller.service";
import { GuideFormControllerService } from "../../services/form-controller/guide-form-controller.service";
import { ApplicantFormControllerService } from "../../services/form-controller/applicant-form-controller.service";
import { PaymentFormControllerService } from "../../services/form-controller/payment-form-controller.service";
import { SendFileFormControllerService } from "../../services/form-controller/sendfile-form-controller.service";
import { FormControllerService } from "../../services/form-controller/form-controller.service";
import { ServiceProperties } from "../../models/service-properties";
import { BaseError } from "../pages/base-error";
import {
  SystemPropertiesControllerService,
} from "../../services/system-settings/system-properties-controller.service";
import { ApplicationFileFormsContainer } from "../pages/application-file/forms-container";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { LanguageControllerService } from "../../services/language-controller/language-controller.service";
import { WebsocketService } from "../../services/websocket.service";
import { SystemProperties } from "../../models/system-properties";
import { TranslatedBaseError } from "../pages/TranslatedBaseError";
import { Language } from "../../models/language";
import { MonitoringControllerService } from "src/app/services/monitoring-controller/monitoring-controller.service";
import { ServiceControllerService } from "../../services/service-controller/service-controller.service";
import { ExternalServerControllerService } from "../../services/service-controller/external-server-controller.service";
import { ExternalServer } from "../../models/external-server";
import { MappingService } from "../../services/mapping.service";
import { VersionService } from "../../services/version/version.service";

// Service monitoring interfaces and configuration
export interface ServiceStatus {
  key: string;
  displayName: string;
  category: 'core' | 'integration' | 'database' | 'auth' | 'analytics' | 'other';
  health: boolean;
  version: string;
  branch: string;
}

const SERVICE_CONFIG: { key: string; displayName: string; category: ServiceStatus['category'] }[] = [
  // Core Services
  { key: 'bpaFe', displayName: 'BPA FE', category: 'core' },
  { key: 'bpaBe', displayName: 'BPA BE', category: 'core' },
  { key: 'bpaWs', displayName: 'BPA WS', category: 'core' },
  { key: 'dsBe', displayName: 'DS BE', category: 'core' },
  { key: 'dsFe', displayName: 'DS FE', category: 'core' },
  // Integration Services
  { key: 'camunda', displayName: 'Camunda', category: 'integration' },
  { key: 'mule', displayName: 'Mule', category: 'integration' },
  { key: 'dataweave', displayName: 'DataWeave', category: 'integration' },
  // Database Services
  { key: 'gdb', displayName: 'GDB', category: 'database' },
  { key: 'restheart', displayName: 'RESTHeart', category: 'database' },
  { key: 'activemq', displayName: 'ActiveMQ', category: 'database' },
  // Auth & Security
  { key: 'keycloak', displayName: 'Keycloak', category: 'auth' },
  // Analytics
  { key: 'statisticsBe', displayName: 'Statistics BE', category: 'analytics' },
  { key: 'statisticsFe', displayName: 'Statistics FE', category: 'analytics' },
  // Other Services
  { key: 'jsAssistant', displayName: 'JS Assistant', category: 'other' },
  { key: 'graylog', displayName: 'Graylog', category: 'other' },
  { key: 'formio', displayName: 'Formio', category: 'other' },
];

@Component({
  selector: "app-sidenav",
  templateUrl: "./sidenav.component.html",
  styleUrls: ["./sidenav.component.scss"],

  providers: [RoleControllerService,
    FormControllerService,
    ServicePropertiesControllerService,
    GuideFormControllerService,
    ApplicantFormControllerService,
    PaymentFormControllerService,
    SendFileFormControllerService,
    ToastrService,
  ],
})
export class SidenavComponent extends TranslatedBaseError implements OnInit {

  @Input() public serviceId: string;
  @Input() public serviceName: string;
  @Input() public defaultMyAccountServiceId: string;
  @Input() public userName: string = "";
  @Input() public userEmail: string = "";
  @Input() public userInitial: string = "";
  @Input() public logout: () => Promise<void> | boolean;
  @Input() public systemProperties: any = { value: new SystemProperties() };

  public isCollapsed: boolean = true;
  public isTablesCollapsed: boolean = true;
  public isCrossTablesCollapsed: boolean = true;
  public isRegistrationCollapsed: boolean = true;
  public isRolesCollapsed: boolean = true;
  public isServicesCollapsed: boolean = true;
  public isApplicationCollapsed: boolean = true;

  public applicationFileData: any[];
  public tablesData: any[];
  public templatesData: any[];
  public crossedTablesData: any[];
  public registrationData: any[];
  public rolesData: any[];
  public botsData: any[];
  public settingsData: any[];
  public newService: Service = new Service();
  public services: Service[];

  public isApplicationFileSection: boolean;
  public isTablesSection: boolean;
  public isTemplatesSection: boolean;
  public isCrossedTablesSection: boolean;
  public isRegistrationSection: boolean;
  public isRolesSection: boolean;
  public isServicesSection: boolean;
  public isBotsSection: boolean;
  public isSettingsSection: boolean;
  public homelink: string;
  public applicationFilePage: string;
  public guideActive: boolean = false;
  public guideFormActive: boolean = false;
  public formActive: boolean = false;
  public paymentActive: boolean = false;
  public sendFileActive: boolean = false;
  public scrollVisible: boolean = false;
  public allForms: any[];
  // hack - angular does not support routerlinks properly

  public isLocal: boolean = environment.LOCAL;
  public env: any = { ...environment };
  public gUp: number = 1;

  // Service monitoring map - stores health/version for all services
  public serviceStatuses: Map<string, ServiceStatus> = new Map();
  public isServicesDrawerClosed: boolean = true;

  // Backward compatibility getters for templates that might still use old properties
  get beVersion(): string { return this.serviceStatuses.get('bpaBe')?.version ?? '?'; }
  get feVersion(): string { return this.serviceStatuses.get('bpaFe')?.version ?? '?'; }
  get dsBeVersion(): string { return this.serviceStatuses.get('dsBe')?.version ?? '?'; }
  get dsFeVersion(): string { return this.serviceStatuses.get('dsFe')?.version ?? '?'; }
  get beUp(): boolean { return this.serviceStatuses.get('bpaBe')?.health ?? false; }
  get feUp(): boolean { return this.serviceStatuses.get('bpaFe')?.health ?? false; }
  get dsBeUp(): boolean { return this.serviceStatuses.get('dsBe')?.health ?? false; }
  get dsFeUp(): boolean { return this.serviceStatuses.get('dsFe')?.health ?? false; }
  get beBranch(): string { return this.serviceStatuses.get('bpaBe')?.branch ?? '?'; }
  get feBranch(): string { return this.serviceStatuses.get('bpaFe')?.branch ?? '?'; }
  get dsBeBranch(): string { return this.serviceStatuses.get('dsBe')?.branch ?? '?'; }
  get dsFeBranch(): string { return this.serviceStatuses.get('dsFe')?.branch ?? '?'; }
  public usersOnCurrentPage: string = "";
  public currentlyLoggedInUsers: string = "";
  public connectedUsers: any[] = [];
  public systemUsers: any[] = [];
  public urlsToUsers: any[] = [];
  public pageOwner: any;

  public bpaDeploy: any = { systemId: "bpa_client" };

  private subscription: Subscription;
  public currentlyLoggedInCount: number = 1;
  public currentOnPageUsersCount: number = 0;
  private languages: Language[];
  public langIsRTL: boolean = false;
  public formId: boolean = false;

  public askingForPage: boolean = false;
  public askForPage: boolean = false;
  public pageAccessRequested: boolean = false;
  public pageAccessDenied: boolean = false;
  public showPageRequest: boolean = false;
  public askingUser: string = "-";
  public foundDrawer: boolean = true;

  public counter: number = 30;

  public userResponseTimeout: any;
  public inactiveUserTimeOut: any;
  public counterTimeOut: any;
  public static feMonitor;

  public serviceSavedName: string;
  public externalServers: ExternalServer[] = [];

  @ViewChild("stickySidenav")
  public stickySidenav: ElementRef;

  constructor(private router: Router,
    public store: Store,
    private formControllerService: FormControllerService,
    private servicePropertiesControllerService: ServicePropertiesControllerService,
    private systemPropertiesControllerService: SystemPropertiesControllerService,
    private activatedRoute: ActivatedRoute,
    private monitoringControllerService: MonitoringControllerService,
    public translateService: TranslateService,
    private languageService: LanguageControllerService,
    private serviceControllerService: ServiceControllerService,
    private externalServerService: ExternalServerControllerService,
    private webSocket: WebsocketService,
    public mappingService: MappingService,
    private cdr: ChangeDetectorRef,
    versionService: VersionService,
    toastr: ToastrService) {
    super(toastr, translateService);
    if (versionService.BUILD_TYPE) {
      this.env.BUILD_TYPE = versionService.BUILD_TYPE;
    }
    if (versionService.EREGISTRATIONS_VERSION) {
      this.env.EREGISTRATIONS_VERSION = versionService.EREGISTRATIONS_VERSION;
    }
    if (versionService.BRANCH_NAME) {
      this.env.BRANCH_NAME = versionService.BRANCH_NAME;
    }
    if (versionService.BUILD_NUMBER) {
      this.env.BUILD_NUMBER = versionService.BUILD_NUMBER;
    }

    const defaultLang = this.translateService.currentLang || store.state.common.lang || this.translateService.getDefaultLang();
    translateService.setDefaultLang(defaultLang);
    languageService.getLanguages().subscribe(
      (data) => {
        this.languages = data;
        const supportedLangCodes = data.map((l) => l.code);
        translateService.langs = [];
        // @ts-ignore
        translateService._$supportedLangs = supportedLangCodes;
        translateService.addLangs(supportedLangCodes);
        let currentLangCode = defaultLang;
        if (!supportedLangCodes.includes(currentLangCode) && supportedLangCodes[0]) {
          const index = translateService.langs.indexOf(currentLangCode);
          currentLangCode = supportedLangCodes[0];
          translateService.setDefaultLang(currentLangCode);
          translateService.store.defaultLang = currentLangCode;
          this.changeLanguage(currentLangCode);
          store.state.common.lang = currentLangCode;
          if (index !== -1) {
            translateService.langs.splice(index, 1);
          }
        } else {
          this.checkRtlHTML();
        }
      });
    this.setupCurrentlyLoggedUsers(null);
  }

  private async checkRtlHTML() {
    const currentLangCode = this.translateService.currentLang || this.translateService.getDefaultLang();
    let currentLanguage = this.languages.find((l) => l.code === currentLangCode);
    if (!currentLanguage) {
      this.languages = await this.languageService.getLanguages().toPromise();
      currentLanguage = this.languages.find((l) => l.code === currentLangCode);
    }
    if (currentLanguage && currentLanguage.rtllanguage) {
      window.document.dir = "rtl";
      this.langIsRTL = true;
    } else {
      window.document.dir = undefined;
      this.langIsRTL = false;
    }
  }

  public containsUrl(coll: any[], url: string) {
    return coll.some((it) => url.includes(it.link));
  }

  public closeLevel2() {
    this.isCollapsed = true;
  }

  public isScrollVisible(elem) {
    if (elem) {
      this.scrollVisible = elem.scrollHeight > elem.clientHeight;
    }
  }

  public getMAServices() {
    this.serviceControllerService.getMyAccountService().subscribe((data) => {
      this.defaultMyAccountServiceId = data.id;
      this.store.state.common.defaultMyAccountServiceId = this.defaultMyAccountServiceId;
    });
  }

  public toggleSideBar() {
    this.store.state.common.sidemenuSmall = !this.store.state.common.sidemenuSmall;
    this.mappingService.refresh();
    this.setSidebarState();
  }

  public setSidebarState() {
    const barstate = this.store.state.common.sidemenuSmall;
    if (barstate === true) {
      document.body.classList.add("sidebar-is-collapsed");
      document.body.classList.remove("sidebar-is-expanded");
    } else {
      document.body.classList.remove("sidebar-is-collapsed");
      document.body.classList.add("sidebar-is-expanded");
    }
  }

  public resetCollapsed() {
    this.isServicesCollapsed = true;
    this.isTablesCollapsed = true;
    this.isCrossTablesCollapsed = true;
    this.isRegistrationCollapsed = true;
    this.isRolesCollapsed = true;
    this.isApplicationCollapsed = true;
    this.isCollapsed = true;
  }

  public toggleServices() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isServicesCollapsed = false;
  }

  public toggleTablesCollapsed() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isTablesCollapsed = false;
  }

  public toggleCrossTablesCollapsed() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isCrossTablesCollapsed = false;
  }

  public toggleRegistrationCollapsed() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isRegistrationCollapsed = false;
  }

  public toggleRolesCollapsed() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isRolesCollapsed = false;
  }

  public toggleApplicationCollapsed() {
    this.resetCollapsed();
    this.isCollapsed = false;
    this.isApplicationCollapsed = false;
  }

  private init() {
    this.registrationData = [];
    this.rolesData = [];
    this.botsData = [];
    this.applicationFileData = [
      { link: `/services/${this.serviceId}/guides`, translation: "nav.2_1_guide", name: "Guide" },
      { link: `/services/${this.serviceId}/forms/guide-form`, translation: "nav.2_1_guide_form", name: "GuideForm" },
      { link: `/services/${this.serviceId}/forms/applicant`, translation: "nav.2_2_form", name: "" },
      { link: `/services/${this.serviceId}/forms/applicant-form`, translation: "nav.2_2_form", name: "" },
      { link: `/services/${this.serviceId || this.defaultMyAccountServiceId}/forms/applicant-form`, translation: "nav.2_2_form_new", name: "ApplicantForm" },
      { link: `/services/${this.serviceId}/forms/document-form`, translation: "nav.2_3_docs", name: "DocumentForm" },
      { link: `/services/${this.serviceId}/forms/payment`, translation: "nav.2_5_payment", name: "PaymentForm" },
      { link: `/services/${this.serviceId}/forms/sendfile`, translation: "nav.2_5_send", name: "SendPageForm" },
    ];
    this.tablesData = [
      { link: `/services/${this.serviceId}/determinantstable`, translation: "nav.annexes-determinants" },
      { link: `/services/${this.serviceId}/documentstable`, translation: "nav.annexes.documents" },
      { link: `/services/${this.serviceId}/coststable`, translation: "nav.annexes.costs" },
      { link: `/services/${this.serviceId}/institutionstable`, translation: "nav.annexes.inst" },
      { link: `/services/${this.serviceId}/fieldstable`, translation: "nav.annexes.fields" },
      { link: `/services/${this.serviceId}/notification`, translation: "nav.guide_messages" },
      // { link: `/translations/${this.serviceId}`, translation: "nav.translations" },
      { link: `/services/${this.serviceId}/lawstable`, translation: "nav.annexes.laws" },
      { link: `/services/${this.serviceId}/locationstable`, translation: "nav.annexes.collection-place" },
      { link: `/services/${this.serviceId}/fieldgeneratortable`, translation: "nav.annexes.fieldgen" },

    ];
    this.templatesData = [
      { link: `/services/${this.serviceId}/templates`, translation: "Templates" },
      { link: `/services/${this.serviceId}/messagestable`, translation: "Templates" },
      { link: `/services/${this.serviceId}/print-documents`, translation: "Templates" },
    ];
    this.crossedTablesData = [
      { link: `/services/${this.serviceId}/costreg`, translation: "nav.annexes.tables.costs_regis" },
      { link: `/services/${this.serviceId}/docsreg`, translation: "nav.annexes.tables.docs_regis" },
      { link: `/services/${this.serviceId}/detreg`, translation: "nav.annexes.tables.dets_regis" },
      { link: `/services/${this.serviceId}/formfieldsreg`, translation: "nav.annexes.tables.formfields_regis" },
      { link: `/services/${this.serviceId}/instreg`, translation: "nav.annexes.tables.inst_regis" },
      { link: `/services/${this.serviceId}/rolereg`, translation: "nav.annexes.tables.roles_regis" },
      { link: `/services/${this.serviceId}/fieldsProps`, translation: "nav.annexes.tables.fields_props" },
      { link: `/services/${this.serviceId}/servicelocreg`, translation: "nav.annexes.tables.service_locations_regis" },
    ];
    this.settingsData = [
      { link: `/translate`, translation: "header.manage_translation" },
      { link: `/settings`, translation: "Instance settings" },
      { link: `/settings/media`, translation: "Media library" },
    ];
    this.chooseSectionByUrlAndRevealIt();
    this.loadApplicationFilePage();
    this.setSidebarState();
  }

  private chooseSectionByUrlAndRevealIt() {
    const url = this.router.url;
    if (this.containsUrl(this.applicationFileData, url) || url.endsWith(`/services/${this.serviceId}/forms/applicant-form`)) {
      this.isApplicationFileSection = true;
      // this.isCollapsed = false;
    }

    if (this.containsUrl(this.tablesData, url)) {
      this.isTablesSection = true;
      // this.isTablesCollapsed = false;
    }

    if (this.containsUrl(this.templatesData, url)) {
      this.isTemplatesSection = true;
      // this.isTablesCollapsed = false;
    }

    if (this.containsUrl(this.crossedTablesData, url)) {
      this.isCrossedTablesSection = true;
      // this.isCrossTablesCollapsed = false;
    }

    if (this.containsUrl(this.registrationData, url) || url.endsWith(`/services/${this.serviceId}/registrations`)) {
      this.isRegistrationSection = true;
      // this.isRegistrationCollapsed = false;
    }

    if (this.containsUrl(this.rolesData, url) || url.endsWith(`/services/${this.serviceId}/roles`)) {
      this.isRolesSection = true;
      // this.isRolesCollapsed = false;
    }

    if (this.containsUrl(this.botsData, url) || url.endsWith(`/services/${this.serviceId}/bots`) || url.includes(`/services/${this.serviceId}/actions`)) {
      this.isBotsSection = true;
      // this.isRolesCollapsed = false;
    }

    if (this.containsUrl(this.settingsData, url)) {
      this.isSettingsSection = true;
    }

    if (url.endsWith("/services") || url === "/") {
      this.isServicesSection = true;
      // this.isCrossTablesCollapsed = false;
    }
  }

  private loadApplicationFilePage() {
    if (this.serviceId || this.defaultMyAccountServiceId) {
      this.loadForm();
    }
  }

  public static formatForm(name: string): string {
    switch (name) {
      case "GUIDE":
        return ApplicationFileFormsContainer.formPaths[0];
      case "PAYMENT PAGE":
      case "PAYMENT":
        return ApplicationFileFormsContainer.formPaths[3];
      case "SEND PAGE":
      case "SENDPAGE":
        return ApplicationFileFormsContainer.formPaths[4];
      case "FORM PAGE":
      case "DOCUMENT":
        return ApplicationFileFormsContainer.formPaths[2];
      default:
      case "APPLICANT":
      case "APPLICANTFORM":
        return ApplicationFileFormsContainer.formPaths[1];
    }
  }

  private loadForm(): void {
    this.applicationFilePage = "applicant-form";
    this.servicePropertiesControllerService.getServiceProperties(this.serviceId || this.defaultMyAccountServiceId).subscribe(
      (props: ServiceProperties) => {
        this.guideActive = props.oldGuideActive;
        if (this.guideActive) {
          if (!this.applicationFilePage) {
            this.applicationFilePage = "guides";

            this.formControllerService.getAllFormStatuses(this.serviceId || this.defaultMyAccountServiceId).subscribe(
              (data) => {
                for (const form of data) {
                  if (form.active) {
                    this.applicationFilePage = SidenavComponent.formatForm(form.name);
                    break;
                  }
                }
              },
            );
          }
        }
      },
    );
  }

  /**
   * Build types that have a dedicated badge colour in _badges.scss. Anything else
   * must fall back explicitly, otherwise the interpolated class simply does not
   * exist and the badge silently inherits the LIVE-looking default.
   */
  private static readonly knownBuildTypes = ["dev", "draft", "beta", "prelive", "live", "rc", "feature"];

  /**
   * Build type shown in the badge. Feature deployments are tagged
   * `FEATURE_<BRANCH>-<VERSION>`, which never fits the sidenav, so collapse the
   * whole family to a plain `FEATURE`. The branch itself moves to the tooltip.
   */
  public get buildType(): string {
    const raw = (this.env && this.env.BUILD_TYPE ? String(this.env.BUILD_TYPE) : "LIVE").trim();
    return this.isFeatureBuild ? "FEATURE" : raw.toUpperCase();
  }

  public get isFeatureBuild(): boolean {
    const raw = this.env && this.env.BUILD_TYPE ? String(this.env.BUILD_TYPE) : "";
    return /^feature[_\-/]/i.test(raw.trim());
  }

  public get buildTypeClass(): string {
    const type = this.buildType.toLowerCase();
    return SidenavComponent.knownBuildTypes.indexOf(type) !== -1 ? `badge-${type}` : "badge-unknown";
  }

  /**
   * Version shown next to the badge. `EREGISTRATIONS_VERSION` is not always a
   * version: it carries the branch name on feature builds and the literal "DEV"
   * on develop. Fall back to the semver build number whenever it is not numeric.
   */
  public get buildVersion(): string {
    const declared = (this.env && this.env.EREGISTRATIONS_VERSION ? String(this.env.EREGISTRATIONS_VERSION) : "").trim();
    if (/^\d+(\.\d+)*$/.test(declared)) {
      return declared;
    }
    const buildNumber = (this.env && this.env.BUILD_NUMBER ? String(this.env.BUILD_NUMBER) : "").trim();
    return buildNumber || declared || "x.y";
  }

  /** Git branch the running build came from; empty when the backend reports none. */
  public get buildBranch(): string {
    return (this.env && this.env.BRANCH_NAME ? String(this.env.BRANCH_NAME) : "").trim();
  }

  public getBuildColor() {
    if (this.env && this.env.BUILD_TYPE) {
      switch (this.env.BUILD_TYPE.toLowerCase()) {
        case "draft":
          return "border-draft";
        case "dev":
          return "border-dev";
        case "beta":
          return "border-beta";
        case "rc":
          return "border-rc";
        case "live":
          return "border-live";
        case "prelive":
          return "border-prelive";
        default:
          return "border-secondary";
      }
    } else {
      return "bg-light";
    }
  }

  public getBuildIcon() {
    if (this.env && this.env.BUILD_TYPE) {
      switch (this.env.BUILD_TYPE.toLowerCase()) {
        case "dev":
          return "fa-code";
          break;
        case "beta":
          return "fa-flask-gear";
          break;
        case "rc":
          return "fa-badge-check";
          break;
        case "live":
          return "fa-globe";
          break;
        default:
          return "fa-code";
          break;
      }
    } else {
      return "fa-code";
    }
  }

  public requestPageAccess() {
    this.askingForPage = true;
    this.showPageRequest = false;
    this.initCounter();
    const currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
    this.webSocket.requestPageAccess(this.userName, currentPath);
  }

  public acceptPageRequest() {
    clearTimeout(this.userResponseTimeout);
    clearInterval(this.counterTimeOut);
    this.pageAccessRequested = false;
    const currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
    this.webSocket.acceptPageAccess(this.userName, currentPath);
    this.router.navigate(["/services/", { backFromForm: true }]);
  }

  public aknowledgePageDenied() {
    this.pageAccessDenied = false;
    this.askingForPage = false;
    const currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
    this.webSocket.aknowledgePageDenied(this.userName, currentPath);
  }

  public denyPageRequest() {
    clearTimeout(this.userResponseTimeout);
    clearInterval(this.counterTimeOut);
    this.pageAccessRequested = false;
    this.resetDrawer();
    const currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
    this.webSocket.denyPageAccess(this.userName, currentPath);
  }

  public dismissPageRequest() {
    clearInterval(this.counterTimeOut);
    this.askingForPage = false;
    const currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
    this.webSocket.dismissPageAccess(this.userName, currentPath);
  }

  public lookForDrawer() {
    const drawer = document.querySelectorAll(".component-settings")[0];
    if (drawer) {
      this.foundDrawer = true;
      drawer.classList.add("hidden");
    }
  }

  public resetDrawer() {
    const drawer = document.querySelectorAll(".component-settings")[0];
    if (drawer) {
      this.foundDrawer = false;
      drawer.classList.remove("hidden");
    }
  }

  public monitorGeneralVersion() {
    const serviceArray = Array.from(this.serviceStatuses.values());
    const healthyCount = serviceArray.filter(s => s.health).length;
    const totalCount = serviceArray.length;

    if (healthyCount === totalCount) {
      this.gUp = 1;   // All healthy
    } else if (healthyCount === 0) {
      this.gUp = -1;  // All down
    } else {
      this.gUp = 0;   // Degraded
    }
  }

  // Initialize service statuses map with default values
  private initializeServiceStatuses(): void {
    SERVICE_CONFIG.forEach(config => {
      this.serviceStatuses.set(config.key, {
        key: config.key,
        displayName: config.displayName,
        category: config.category,
        health: false,
        version: '?',
        branch: '?'
      });
    });
  }

  // Get services filtered by category for template grouping
  public getServicesByCategory(category: string): ServiceStatus[] {
    return Array.from(this.serviceStatuses.values())
      .filter(s => s.category === category);
  }

  // Get all service categories that have at least one service
  public getServiceCategories(): string[] {
    return ['core', 'integration', 'database', 'auth', 'analytics', 'other'];
  }

  // Collapsible category state management
  private openServiceCategories: Set<string> = new Set(['core']); // Core open by default

  public toggleServiceCategory(category: string): void {
    if (this.openServiceCategories.has(category)) {
      this.openServiceCategories.delete(category);
    } else {
      this.openServiceCategories.add(category);
    }
  }

  public isServiceCategoryOpen(category: string): boolean {
    return this.openServiceCategories.has(category);
  }

  public getCategoryIcon(category: string): string {
    const icons: { [key: string]: string } = {
      core: 'fa-server',
      integration: 'fa-plug',
      database: 'fa-database',
      auth: 'fa-shield-keyhole',
      analytics: 'fa-chart-line',
      other: 'fa-cube'
    };
    return icons[category] || 'fa-cube';
  }

  public getCategoryDisplayName(category: string): string {
    const names: { [key: string]: string } = {
      core: 'Core Services',
      integration: 'Integration',
      database: 'Database & Messaging',
      auth: 'Authentication',
      analytics: 'Analytics',
      other: 'Other Services'
    };
    return names[category] || category;
  }

  public getHealthyCountByCategory(category: string): number {
    const services = this.getServicesByCategory(category);
    return services.filter(s => s.health).length;
  }

  public getCategoryHealthBadge(category: string): string {
    const services = this.getServicesByCategory(category);
    const healthyCount = services.filter(s => s.health).length;
    if (healthyCount === services.length) {
      return 'badge-success';
    } else if (healthyCount === 0) {
      return 'badge-danger';
    }
    return 'badge-warning';
  }

  public setupCurrentlyLoggedUsers(users: any) {
    this.currentlyLoggedInUsers = users && Object.keys(users).map((key) => users[key]?.fullName).join(", ") || "";
    this.currentlyLoggedInCount = users && Object.keys(users).length || 1;
  }

  public loadSystemProperties() {
    this.systemPropertiesControllerService.getServiceProperties().subscribe(
      (systemProperties: any) => {
        if (systemProperties) {
          this.bpaDeploy = systemProperties.deployProperties.filter((p) => p.systemId === "bpa_client")[0];
          this.systemProperties.value = systemProperties;
          this.systemProperties.value.siteName = this.systemProperties.value.siteName || "dev";
          this.homelink = systemProperties.homepageUrl ? systemProperties.homepageUrl : environment.E_REGISTRATIONS_HOME_URL;
        }
      },
    );
  }

  public toggleDeployEnabled() {
    if (!this.bpaDeploy) {
      console.error("bpaDeploy is not defined");
      this.showErrorMessage({ message: "Deploy properties not loaded. Please refresh the page." });
      return;
    }

    // Get the actual system properties object (handle both wrapped and unwrapped cases)
    const sysProps = this.systemProperties?.value || this.systemProperties;

    if (!sysProps || !sysProps.deployProperties) {
      console.error("System properties or deployProperties is not defined", sysProps);
      this.showErrorMessage({ message: "System properties not loaded. Please refresh the page." });
      return;
    }

    // Store the current value before toggle
    const oldValue = this.bpaDeploy.deployEnabled;

    // Toggle the deployEnabled flag
    this.bpaDeploy.deployEnabled = !this.bpaDeploy.deployEnabled;

    // Also ensure the change is reflected in the systemProperties array
    const deployIndex = sysProps.deployProperties.findIndex((p) => p.systemId === "bpa_client");
    if (deployIndex !== -1) {
      sysProps.deployProperties[deployIndex].deployEnabled = this.bpaDeploy.deployEnabled;
    }

    // Manually trigger change detection to ensure the view updates
    this.cdr.detectChanges();

    // Call the update method
    this.updateSystemProps();
  }

  public updateSystemProps() {
    // Get the actual system properties object (handle both wrapped and unwrapped cases)
    const sysProps = this.systemProperties?.value || this.systemProperties;

    this.systemPropertiesControllerService.updateServiceProperties(sysProps).subscribe(
      (data) => {
        this.responseSuccessful("System properties updated");
      },
      (error) => {
        console.error("Failed to update system properties:", error);
        this.showErrorMessage(error);
      },
    );
  }

  private formatCurrentPageUsers(users, loggedInUsers): string {
    let usersInPage = "";
    if (users && users.length) {
      for (let u = 0; u < users.length; u++) {
        const fullName = loggedInUsers && loggedInUsers[users[u].username]?.fullName;
        usersInPage += (fullName || users[u].username || users[u].email) + " ";
      }
    }
    return usersInPage;
  }

  private getCurrentUserTabsNumber(username, users) {
    const nbUser = users.filter((u) => {
      return u.username === username;
    });
    return nbUser.length;
  }

  public formatUserName(usr) {
    const fullName = this.connectedUsers && this.connectedUsers[usr?.username]?.fullName;
    return (fullName || usr?.username || usr?.email || "unknown user");
  }

  private connectWebsocket() {
    let previousUser = "";
    this.subscription = this.webSocket.connect().subscribe(
      (message) => {
        try {
          if (message) {
            if (message.users) {
              this.setupCurrentlyLoggedUsers(message.users);
            }
            let currentPath = "/" + this.activatedRoute.snapshot.url.join("/");
            let index = currentPath.indexOf("#");
            if (index !== -1) {
              currentPath = currentPath.substring(0, index);
            }
            index = currentPath.indexOf("%23");
            if (index !== -1) {
              currentPath = currentPath.substring(0, index);
            }
            // console.log("--- currentPath", currentPath);
            if (message.urlsToUsers && message.urlsToUsers[currentPath]) {
              this.urlsToUsers = message.urlsToUsers[currentPath] || [];
              this.connectedUsers = message.users || [];
              this.systemUsers = Object.values(this.connectedUsers);
              // console.log(this.systemUsers);
              this.usersOnCurrentPage = this.formatCurrentPageUsers(this.urlsToUsers, message.users);
              this.currentOnPageUsersCount = this.urlsToUsers.length;
              this.pageOwner = this.urlsToUsers.find((u) => u.ownsPage);
              const currentUser = this.urlsToUsers.find((u) => u.connId === message.connId);
              if ((currentUser.connId !== this.pageOwner?.connId) && this.checkIfpageIsAskable()) {
                this.store.state.common.lockedPageByUser = true;
                this.askForPage = true;
                previousUser = this.pageOwner?.connId;
                if (currentUser.askedToLeaveDenied) {
                  this.pageAccessDenied = true;
                  this.askingForPage = false;
                }
                if (!this.askingForPage) {
                  this.showPageRequest = true;
                }
              } else {
                this.store.state.common.lockedPageByUser = false;
                this.askForPage = false;
                this.showPageRequest = false;
                if (currentUser.connId === this.pageOwner?.connId && this.pageOwner?.askedToLeaveBy) {
                  this.pageAccessRequested = true;
                  this.lookForDrawer();
                  this.initCounter();
                  this.forceUnrespondingUser();
                  this.askingUser = this.pageOwner?.askedToLeaveBy?.username;
                } else if (currentUser.connId === this.pageOwner?.connId && !(this.pageOwner?.askedToLeaveBy)) {
                  clearTimeout(this.userResponseTimeout);
                  clearInterval(this.counterTimeOut);
                  this.pageAccessRequested = false;
                  this.askingForPage = false;
                  this.askingUser = "-";
                }
                if (previousUser && previousUser !== currentUser.connId && this.formId) {
                  location.reload();
                }
              }
            }
            if (message.currentlyPublishedCount) {
              this.store.state.common.runningPublications = Number(message.currentlyPublishedCount);
            } else {
              this.store.state.common.runningPublications = 0;
            }
            this.store.state.common.lockPublishButton = message.publishes && this.serviceId && message.publishes[this.serviceId];
            if (message.publishes) {
              this.store.state.common.currentlyPublishingUsers = "";
              Object.entries(message.publishes).forEach(([key, value]) => {
                // @ts-ignore
                this.store.state.common.currentlyPublishingUsers += `<br>${value.serviceName} - ${value.userName.split("(")[0]}`;
              });
            }
            if (message.services) {
              // Update all services dynamically from WebSocket message
              Object.keys(message.services).forEach(serviceKey => {
                const serviceData = message.services[serviceKey];
                const existing = this.serviceStatuses.get(serviceKey);
                if (existing) {
                  existing.health = serviceData.health ?? false;
                  existing.version = serviceData.version ?? '?';
                  existing.branch = serviceData.branch ?? '?';
                }
              });
              this.monitorGeneralVersion();
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.trace("websocket message processing failed", e);
          this.websocketConnectErrorCb();
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.trace("websocket connection broke", err);
        this.websocketConnectErrorCb();
      });
  }

  public websocketConnectErrorCb() {
    try {
      this.currentlyLoggedInCount = 1;
      this.currentlyLoggedInUsers = "";
      this.currentOnPageUsersCount = 0;

      this.askingForPage = false;
      this.askForPage = false;
      this.pageAccessRequested = false;
      this.pageAccessDenied = false;
      this.showPageRequest = false;
      this.askingUser = "-";
      this.usersOnCurrentPage = "";

      this.store.state.common.currentlyPublishingUsers = "";
      this.store.state.common.lockedPageByUser = false;
      this.store.state.common.lockPublishButton = false;
      this.store.state.common.runningPublications = 0;
      clearTimeout(this.userResponseTimeout);
      clearInterval(this.counterTimeOut);
      this.webSocket.closeConnection();
      // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
    } catch (e) {
      // eslint-disable-next-line no-console
      console.trace("websocket error", e);
    }
    setTimeout(this.connectWebsocket.bind(this), 3000);
  }

  public monitorFe() {
    // reason for this monitoring to be here is for user not getting logged out while being on idle
    this.monitoringControllerService.getFEStatus().subscribe((data) => {
      const feService = this.serviceStatuses.get('bpaFe');
      if (feService) {
        feService.health = (data && data.status && data.status === "up");
      }
      SidenavComponent.feMonitor = setTimeout(() => this.monitorFe(), 60000);
    }, (error) => {
      const feService = this.serviceStatuses.get('bpaFe');
      if (feService) {
        feService.health = false;
      }
      SidenavComponent.feMonitor = setTimeout(() => this.monitorFe(), 60000);
    });
  }

  public checkIfpageIsAskable() {
    const loackablePaths = ["forms", "roles", "bots", "templates", "print-documents"];
    const currentPath = this.activatedRoute?.snapshot?.url[2]?.path;
    const isPath = loackablePaths.some((p) => {
      return p === currentPath;
    });
    // console.log("--- check Page askable", this.pageOwner, currentPath, this.activatedRoute.snapshot, this.activatedRoute.snapshot.url[3]?.path !== undefined, isPath, this.activatedRoute.snapshot.url[3]?.path !== undefined && isPath);
    return this.activatedRoute.snapshot.url[3]?.path !== undefined && isPath;
  }

  public ngOnInit() {
    this.initializeServiceStatuses();
    this.loadSystemProperties();
    this.isScrollVisible(this.stickySidenav);
    this.init();
    this.toggleBar();
    this.loadExternalServers();
    if (!this.isLocal) {
      this.connectWebsocket();
      clearTimeout(SidenavComponent.feMonitor);
      this.monitorFe();
    }
    this.activatedRoute.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.formId = (params.formId || params.roleId || params.documentId) ? true : false;
      this.chooseSectionByUrlAndRevealIt();
      // to put back to check user inactivity
      /*
      if (this.formId) {
        this.checkTimeOut();
      }
      if (params.backFromForm && params.backFromForm === "true") {
        this.simpleErrorMessage("You've been redirect due to inactivity");
      }
      */
    });
    this.loadSystemProperties();
    setTimeout(() => {
      this.initLevelUser();
    });
    this.checkIfpageIsAskable();

    this.defaultMyAccountServiceId = this.store.state.common.defaultMyAccountServiceId;
    if (!this.defaultMyAccountServiceId) {
      this.getMAServices();
    }
  }

  public loadExternalServers() {
    this.externalServerService.getExternalServers().subscribe(
      (data: ExternalServer[]) => {
        this.externalServers = data;
      },
      (error) => this.showErrorMessage(error),
    );
  }

  public initLevelUser() {
    // eslint-disable-next-line
    if (this.store.state.common.userLevels.get("SideNavLevel") === false) {
      // eslint-disable-next-line
      this.store.state.common.userLevels.pages.SideNavLevel = 1;
    } else {
      // eslint-disable-next-line
      this.store.state.common.userLevels.pages.SideNavLevel = this.store.state.common.userLevels.get("SideNavLevel");
    }
  }

  public checkTimeOut() {
    this.inactiveUserTimeOut = setTimeout(() => {
      this.rerouteUser();
    }, 600000);
  }

  public forceUnrespondingUser() {
    this.userResponseTimeout = setTimeout(() => {
      this.acceptPageRequest();
    }, 30000);
  }

  public initCounter() {
    this.counter = 30;
    if (this.counterTimeOut) {
      clearInterval(this.counterTimeOut);
    }
    this.counterTimeOut = setInterval(() => {
      this.counter--;
      if (this.counter <= 0) {
        this.counter = 30;
        clearInterval(this.counterTimeOut);
      }
    }, 1000);
  }

  @HostListener("window:keydown")
  @HostListener("window:mousedown")
  @HostListener("window:mousemove")
  public checkUserActivity() {
    if (this.formId && this.inactiveUserTimeOut) {
      clearTimeout(this.inactiveUserTimeOut);
      this.checkTimeOut();
    }
  }

  public rerouteUser() {
    this.router.navigate(["/services/", { backFromForm: true }]);
  }

  public toggleBar() {
    const barstate = this.store.state.common.topBarCollapsed;
    if (barstate === true) {
      document.body.classList.add("topbar-is-collapsed");
      document.body.classList.remove("topbar-is-expanded");
    } else {
      document.body.classList.remove("topbar-is-collapsed");
      document.body.classList.add("topbar-is-expanded");
    }
  }

  public ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public changeLanguage(langCode: string) {
    this.translateService.use(langCode).subscribe(() => {
      this.store.state.common.lang = langCode;
      return this.checkRtlHTML();
    });
    return false;
  }

}
